import { ChevronDown, LifeBuoy, Search, SearchX, X } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import type { PlayerRoute } from '../../../app/appStore';
import { useAppStore } from '../../../app/appStore';
import { useCountryStore } from '../../../shared/country/countryStore';
import { marketCopyKey } from '../../../shared/country/marketCopy';
import { useI18nStore } from '../../../shared/i18n/i18nStore';
import type { MessageKey } from '../../../shared/i18n/messages';
import { FaqIllustration } from './FaqIllustrations';
import { FAQ_CATEGORIES, FAQ_ENTRIES, faqKeys, type FaqCategory, type FaqEntry } from './faqCatalog';
import { highlight, matchesTerms, queryTerms, splitList } from './faqSearch';

type CategoryFilter = FaqCategory | 'all';

/** Marks the searched-for words inside a rendered string. */
function Marked({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) return <>{text}</>;
  return (
    <>
      {highlight(text, terms).map((segment, index) => (
        <Fragment key={index}>{segment.hit ? <mark>{segment.text}</mark> : segment.text}</Fragment>
      ))}
    </>
  );
}

export function FaqPanel() {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  const setRoute = useAppStore((state) => state.setRoute);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const terms = useMemo(() => queryTerms(query), [query]);
  const searching = terms.length > 0;

  // Aliases are in here too, which is the whole point: they are never shown but
  // they are what lets a user's own words reach the answer.
  const haystacks = useMemo(() => {
    return new Map(
      FAQ_ENTRIES.map((entry) => {
        const keys = faqKeys(entry, countryCode);
        const text = [keys.question, keys.tldr, keys.points, keys.aliases].map((key) => t(key)).join(' • ');
        return [entry.id, text];
      }),
    );
  }, [t, countryCode]);

  const hits = useMemo(
    () => FAQ_ENTRIES.filter((entry) => matchesTerms(haystacks.get(entry.id) ?? '', terms)),
    [haystacks, terms],
  );

  const countByCategory = useMemo(() => {
    const counts = new Map<CategoryFilter, number>([['all', hits.length]]);
    for (const entry of hits) counts.set(entry.category, (counts.get(entry.category) ?? 0) + 1);
    return counts;
  }, [hits]);

  const visible = category === 'all' ? hits : hits.filter((entry) => entry.category === category);

  const grouped = FAQ_CATEGORIES.map((meta) => ({
    meta,
    entries: visible.filter((entry) => entry.category === meta.id),
  })).filter((group) => group.entries.length > 0);

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clear = () => {
    setQuery('');
    setExpanded(new Set());
  };

  // A search that hits nothing in the chosen category but something elsewhere
  // is a dead end unless we say so — offer the way out instead of an empty page.
  const strandedByCategory = searching && visible.length === 0 && hits.length > 0;

  return (
    <div className="faq-panel">
      <div className="faq-controls">
        <div className="faq-search">
          <Search size={16} aria-hidden={true} />
          <input
            type="search"
            inputMode="search"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(marketCopyKey('faqSearchPlaceholder', countryCode))}
            aria-label={t(marketCopyKey('faqSearchPlaceholder', countryCode))}
          />
          {query ? (
            <button type="button" className="faq-search-clear" onClick={clear} aria-label={t('faqSearchClear')}>
              <X size={15} aria-hidden={true} />
            </button>
          ) : null}
        </div>

        <nav className="faq-chips" aria-label={t('faqTab')}>
          {([{ id: 'all' as const, label: 'faqCat_all' as MessageKey, emoji: '' }, ...FAQ_CATEGORIES]).map((meta) => {
            const count = countByCategory.get(meta.id) ?? 0;
            return (
              <button
                key={meta.id}
                type="button"
                className={category === meta.id ? 'active' : ''}
                onClick={() => setCategory(meta.id)}
                disabled={count === 0 && meta.id !== category}
                aria-pressed={category === meta.id}
              >
                {meta.emoji ? <span aria-hidden={true}>{meta.emoji}</span> : null}
                {t(meta.label)}
                <b>{count}</b>
              </button>
            );
          })}
        </nav>

        {searching ? (
          <p className="faq-result-count" aria-live="polite">
            {t('faqResultCount').replace('%n%', String(hits.length))}
          </p>
        ) : null}
      </div>

      {grouped.map((group) => (
        <section key={group.meta.id} className="faq-group">
          <h3 className="faq-group-header">
            <span aria-hidden={true}>{group.meta.emoji}</span>
            {t(group.meta.label)}
            <b>{group.entries.length}</b>
          </h3>
          {group.entries.map((entry) => (
            <FaqCard
              key={entry.id}
              entry={entry}
              terms={terms}
              // A search result that stays collapsed hides the very words the
              // user searched for, so matches open themselves.
              open={searching || expanded.has(entry.id)}
              onToggle={() => toggle(entry.id)}
              onNavigate={setRoute}
            />
          ))}
        </section>
      ))}

      {visible.length === 0 ? (
        <div className="faq-empty">
          <SearchX size={30} aria-hidden={true} />
          <strong>{t('faqEmptyTitle')}</strong>
          {strandedByCategory ? (
            <>
              <p>{t('faqEmptyOtherCategories').replace('%n%', String(hits.length))}</p>
              <button type="button" className="faq-empty-cta secondary" onClick={() => setCategory('all')}>
                {t('faqCat_all')}
              </button>
            </>
          ) : (
            <p>{t('faqEmptyBody')}</p>
          )}
          <button type="button" className="faq-empty-cta" onClick={() => setRoute('avisos.help')}>
            <LifeBuoy size={16} aria-hidden={true} />
            {t('faqEmptyCta')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function FaqCard({
  entry,
  terms,
  open,
  onToggle,
  onNavigate,
}: {
  entry: FaqEntry;
  terms: string[];
  open: boolean;
  onToggle: () => void;
  onNavigate: (route: PlayerRoute) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  const keys = faqKeys(entry, countryCode);
  const points = splitList(t(keys.points));
  const figureLabels = splitList(t(keys.figure));

  return (
    <details className="faq-card" open={open}>
      <summary
        className="faq-card-q"
        onClick={(event) => {
          event.preventDefault();
          onToggle();
        }}
      >
        <span className="faq-card-q-text">
          <Marked text={t(keys.question)} terms={terms} />
        </span>
        <ChevronDown size={16} className="faq-card-chevron" aria-hidden={true} />
      </summary>

      <div className="faq-card-a">
        <p className="faq-card-tldr">
          <span aria-hidden={true}>💡</span>
          <span>
            <Marked text={t(keys.tldr)} terms={terms} />
          </span>
        </p>

        <FaqIllustration id={entry.id} labels={figureLabels} />

        <ol className="faq-card-points">
          {points.map((point, index) => (
            <li key={index}>
              <Marked text={point} terms={terms} />
            </li>
          ))}
        </ol>

        {entry.action ? (
          <button type="button" className="faq-card-action" onClick={() => onNavigate(entry.action!)}>
            {t(keys.cta)}
          </button>
        ) : null}
      </div>
    </details>
  );
}
